/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {IconButton, Tooltip, Typography} from '@mui/material';
import Link from '@mui/material/Link';
import {cloneDeep} from 'lodash';
import {MRT_ColumnDef, MRT_Row} from 'material-react-table';
import React, {useEffect, useMemo, useState} from 'react';
import {tableHeaderGray} from '../../../../../../../constants/colors';
import VAR_COMMON_CONSTANTS from "../../../../../../../constants/globalVariables";
import {useHasWriteAccess} from '../../../../../../../redux/ducks/AccessControl/accessControlHelpers';
import {selectDatasetsExecutionStatus, selectRedlineDatasetIdsToData} from "../../../../../../../redux/ducks/CoverageRequirements/slice";
import {useAppSelector} from "../../../../../../../redux/hooks";
import ArgosFlexbox from '../../../../../../components/Containers/ArgosFlexbox';
import ArgosDialog, {IArgosDialogProps} from "../../../../../../components/Dialogs/ArgosDialog";
import RedlineAppDialog from '../../../../../../components/Dialogs/RedlineAppDialogs/RedlineAppDialog';
import {ArgosAddIcon, ArgosDeleteIcon, ArgosEditIcon, ArgosDeleteAllIcon, ArgosFolderDeleteIcon} from '../../../../../../components/Icons/ArgosActionIcons';
import Loader from "../../../../../../components/Loader/Loader";
import AddTreeNodeDialog, {ERequirementsToolTreeDialogAppNames} from "../../../../../../components/Tree/Parts/AddTreeNodeDialog";
import {coverageRequirementsDetails, DatasetType} from '../../../../constants';
import {
  COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_NAME_URL_PARAM,
  COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM,
  COVERAGE_REQUIREMENTS_STUDY_ID_URL_PARAM,
  IN_PROGRESS_STATUSES,
  KEY_NAME_SOURCE_DATASET,
  REDLINE_DIALOG_OPTIONS,
  SELECTED_DATASET_ID_URL_PARAM,
  SINGLE_DATASET_TYPE_STR
} from '../../../constants';
import {SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS} from '../../../../../SensingSystemArchitectureDesign/constants'; 
import {DrivingScenarioPropertiesRow, fetchRwoDataEndpoint, LOADER_WRAPPER_STYLE, PROPERTY_TYPE_IS_MULTIPLE_SELECT} from './constants';
import {PropertiesData} from './RightPanelCoverageRequirements';
import {IFetchObjectsResponse, IProcessedObjectData, TPmoAndMiscProfileNames} from "../../../../../../../constants/objects/ObjectTypes/common";
import {getObjVersionDisplayString, getProfileDisplayName, processObjectData} from "../../../../../../../helpers/objects/helpers";
import { AdasProgramsReportPages } from "../../../../../AdasPrograms/ItemDetails/common/constants";
import {ArgosTableSimple} from "../../../../../../components/Table/ArgosTableSimple";
import { usePostRequest } from '../../../../../../../api/helper';

/**
 * Renders a link if the provided link is not undefined or empty.
 * If the link is undefined or empty, it renders the text without a link.
 *
 * @param {string | undefined} text - The text to display.
 * @param {string | undefined} link - The URL to link to.
 * @returns {JSX.Element} - A JSX element containing the text, optionally wrapped in a link.
 */
export function renderLink(text: string | undefined, link: string | undefined) {
  if (link === undefined || link === "") {
    return (
      <ArgosFlexbox>
        <Typography sx={{fontSize: 14}}>
          {text}
        </Typography>
      </ArgosFlexbox>
    )
  } else {
    const encodedLink = encodeURI(link)
    return (
      <ArgosFlexbox>
        <Link underline="hover" target="_blank">
          <span
            style={{cursor: 'pointer'}}
            onClick={() => window.open(encodedLink, '_blank')}
          >
            {text}
          </span>
        </Link>
      </ArgosFlexbox>
    )
  }
}

/**
 * Generates a URL for a dataset based on the provided parameters.
 *
 * @param {number} rawOrMerged - Indicates whether the dataset is raw (0) or merged (1).
 * @param {string} studyId - The ID of the study.
 * @param {string} datasetId - The ID of the dataset.
 * @param {string} [page] - Optional parameter to specify the page type.
 * @returns {string} - The generated URL for the dataset.
 */
export const getUrlForDataset = (rawOrMerged: number, studyId: string, datasetId: string, page:AdasProgramsReportPages | null = null) => {
  let url=''
  // If the page is 'sensorComponentRequirement', use the VIEW_STUDY_ROUTE
  // Otherwise, use the coverageRequirementsDetails URL with query parameters
  if(page && page===AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT) {
    url = `${SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.route}`
      + `?id=${studyId}`
  }
  else{
    const rawMergedString = rawOrMerged === 0 ? "Raw " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS : "Merged " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS
    url = `${coverageRequirementsDetails}`
      + `?${SELECTED_DATASET_ID_URL_PARAM}=${datasetId}`
      + `&${COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_NAME_URL_PARAM}=Coverage Analysis`
      + `&${COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM}=${rawMergedString}`
      + `&${COVERAGE_REQUIREMENTS_STUDY_ID_URL_PARAM}=${studyId}`
  }
  return url
}

const minMaxWidth = '40vw'
export const ADAS_DIALOG_STYLE: Partial<IArgosDialogProps> = {
  // maxWidth={'fit-content'}
  minWidth: minMaxWidth,
  maxWidth: minMaxWidth,
  height: '60vh',
  headerTitleAlign: 'left',
}

interface Props {
  studyId: string;
  selectedDatasetId: string;
  propertiesFlatList: PropertiesData[];
  isReadOnlyStudy?: boolean;
  isLoading?: boolean;
  loadedPropertiesData?: DrivingScenarioPropertiesRow[] | null;
  saveNewInfo?: any;
  datasetType?: DatasetType;
}

const DrivingScenarioProperties: React.FC<Props> = (props: any) => {
  const [data, setData] = useState<DrivingScenarioPropertiesRow[]>(props?.loadedPropertiesData);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedDialog, setSelectedDialog] = useState("");
  const [isAddProperty, setIsAddProperty] = useState(false);
  const [currentDataToEdit, setCurrentDataToEdit] = useState<any>({});
  const [dialogTitle, setDialogTitle] = useState<any>();
  const [propertyType, setPropertyType] = useState<string>("")
  const hasWriteAccess = useHasWriteAccess()
  const datasetType: DatasetType = props?.datasetType
  const selectedDatasetExecutionStatus: any = useAppSelector((state) => selectDatasetsExecutionStatus(state)?.[props?.selectedDatasetId])
  const shouldDisableRowOnInProgress = IN_PROGRESS_STATUSES.has(selectedDatasetExecutionStatus)
  const [isAddTreeNodeDialogOpen, setIsAddTreeNodeDialogOpen] = useState(false)
  const [adasProgramId, setAdasProgramId] = useState<string>("")
  const [programName, setProgramName] = useState<string>("")
  const isReadOnlyStudy: boolean = props?.isReadOnlyStudy
  const redlineDatasetIdsToData = useAppSelector(selectRedlineDatasetIdsToData)

  // Setup for fetching object data
  const fetchObjectsHandle = usePostRequest(fetchRwoDataEndpoint)
  /**
   * Fetches object data and processes it to set the state.
   * This function fetches the objects from the redline dataset and processes them to create a mapping of object categories to their data.
   */
  const fetchObjectData = () => {
    fetchObjectsHandle().then((fetchObjectsResponse: IFetchObjectsResponse) => {
      const processedObjectData = processObjectData(fetchObjectsResponse)
      setObjCatToData(processedObjectData);
    }).catch((err) => {
      console.log(err)
    })
  }
  // State to store the processed object data
  const [objCatToData, setObjCatToData] = useState<IProcessedObjectData>({})

  useEffect(() => {
    fetchObjectData()
    setData(props?.loadedPropertiesData)
  }, [props?.loadedPropertiesData])

  function isObj(row: MRT_Row<DrivingScenarioPropertiesRow>) {
    return "objectId" in row?.original
  }

  function hasLink(row: MRT_Row<DrivingScenarioPropertiesRow>) {
    return row?.original?.property_link !== ""
  }

  const renderObjCell = (row: MRT_Row<DrivingScenarioPropertiesRow>) => {
    const objName = row.original?.name ?? null
    const objCategory = row.original?.category ?? null
    const objVariant = row.original?.variantName ?? null
    const objId = row.original.objectId

    // Get the version from the object data given the object ID
    const versionString: string = getObjVersionDisplayString(objId, objCatToData);

    const cellValue = getObjectDisplayName(objCategory, objName, objVariant, versionString);

    return (
      <Link onClick={() => window.open(`/objectDetails?id=${row?.original?.objectId}`, '_blank')} underline="hover" target="_blank" rel="noreferrer">
        {cellValue}
      </Link>
    )
  }

  /**
   * Table definition
   */
  const singleDatasetColumns = useMemo<MRT_ColumnDef<DrivingScenarioPropertiesRow>[]>(
    () => [
      {
        accessorFn: (row: DrivingScenarioPropertiesRow) => row?.subRows ? row.type ?? '' : "",
        id: 'type',
        header: 'Type',
      },
      {
        accessorFn: (row: DrivingScenarioPropertiesRow) => row == undefined ? "" : row.name ?? '',
        id: 'name',
        header: 'Name',
        Cell: ({cell, row}) => {
          if (isObj(row)) {
            return renderObjCell(row)
          } else if (hasLink(row)) {
            return (
              <Link href={row?.original?.property_link} underline="hover" target="_blank" rel="noreferrer">
                {cell.getValue<string>()}
              </Link>
            )
          } else {
            return (
              <div>{cell.getValue<string>()}</div>
            )
          }
        },
        size: 400,
      }
    ],
    [selectedDatasetExecutionStatus, objCatToData]
  )

  const mergedDatasetColumns = useMemo<MRT_ColumnDef<DrivingScenarioPropertiesRow>[]>(
    () => [
      {
        accessorFn: (row: DrivingScenarioPropertiesRow) => row?.subRows ? row.type ?? '' : "",
        id: 'type',
        header: 'Type',
      },
      {
        accessorFn: (row: DrivingScenarioPropertiesRow) => row == undefined ? "" : row.name ?? '',
        id: 'name',
        header: 'Name',
        Cell: ({cell, row}) => {
          if (isObj(row)) {
            return renderObjCell(row)
          } else if (hasLink(row)) {
            return (
              <Link href={row?.original?.property_link} underline="hover" target="_blank" rel="noreferrer">
                {cell.getValue<string>()}
              </Link>
            )
          } else {
            return (
              <div>{cell.getValue<string>()}</div>
            )
          }
        },
        size: 400,
      },
      {
        accessorFn: (row: DrivingScenarioPropertiesRow) => row == undefined ? "" : row[KEY_NAME_SOURCE_DATASET] ?? '',
        id: KEY_NAME_SOURCE_DATASET,
        header: 'Source ' + VAR_COMMON_CONSTANTS.VAR_DATA_SET,
        Cell: ({row}) => {
          const ids = row?.original?.[KEY_NAME_SOURCE_DATASET]
          return (
            <ArgosFlexbox>
              {ids?.map((id: string) => {
                const name = redlineDatasetIdsToData?.[id]?.name ?? id
                return (
                  renderLink(name, getUrlForDataset(0, props.studyId, id))
                )
              })}
            </ArgosFlexbox>
          )
        },
        size: 400,
      },
    ],
    [props.studyId, redlineDatasetIdsToData]
  )
  useEffect(() => {
    refIsTableRowActionCurrentlyInProgress.current = false
  }, [data]);
  /**
   * Based on action, render appropiate dialog
   */
  const tableRowActions = (row: any) => {
    const adasProgramId = row?.original?.meta?.adasProgramId
    const programName = row?.original?.programName
    const shouldDisableRow = (row?.original.subRows?.length > 0 && row?.original?.type != "Object" && row?.original?.type != "Road Type") || shouldDisableRowOnInProgress
    return (
      row?.original.subRows?.length != undefined ? (
        <ArgosFlexbox>
          <Tooltip title={`Add ${row.original.type} Property`} arrow placement="bottom">
            <span>
              <IconButton
                style={{display: shouldDisableRow ? "none" : "initial"}}
                onClick={() => {
                  setIsAddProperty(true)
                  setPropertyType(row.original.type)
                  setSelectedDialog("addProperty")
                  setDialogTitle(`Add ${row.original.type} Property`)
                  if (row.original.type == "Capability" || row.original.type == "Use Case") {
                    const currentData = {
                      "datasetId": props.selectedDatasetId,
                      "category": row.original.category,
                      "option": row.original.name,
                      "rowData": row.original,
                      "index": row.index
                    }
                    setCurrentDataToEdit(currentData)
                    setProgramName(programName)
                    setAdasProgramId(adasProgramId)
                    setIsAddTreeNodeDialogOpen(true)
                  } else {
                    setCurrentDataToEdit({
                      "datasetId": props.selectedDatasetId,
                    })

                    setIsOpenDialog(true)
                  }
                }}
                disabled={
                  !PROPERTY_TYPE_IS_MULTIPLE_SELECT[row.original.type as keyof object]
                  && (row.original.subRows.length === 1) || shouldDisableRowOnInProgress || !hasWriteAccess
                }
                size="medium"
              >
                <ArgosAddIcon/>
              </IconButton>
            </span>
          </Tooltip>
          {row?.original.subRows?.length > 1 && <Tooltip title="Delete" arrow placement='bottom'>
          <IconButton
              style={{display: shouldDisableRow ? "none" : "initial"}}
              disabled={
                !PROPERTY_TYPE_IS_MULTIPLE_SELECT[row.original.type as keyof object]
                && (row.original.subRows.length === 1) || shouldDisableRowOnInProgress || !hasWriteAccess
              }
              size="medium"
              onClick={() => handleButtonClicked("deleteProperty", row)}
          >
            <ArgosDeleteAllIcon/>
          </IconButton>
          </Tooltip>}
          {row?.original?.type == "Object" && row?.original.subRows?.length > 1 && <Tooltip title={`Delete Category Groups`} arrow placement='bottom'>
          <IconButton

              style={{display: shouldDisableRow ? "none" : "initial"}}
              onClick={() => {
                setSelectedDialog("delCategoryGroup")
                setDialogTitle(`Delete Category Groups`)
                setCurrentDataToEdit(row)
                setIsOpenDialog(true)
              }}
              disabled={
                !PROPERTY_TYPE_IS_MULTIPLE_SELECT[row.original.type as keyof object]
                && (row.original.subRows.length === 1) || shouldDisableRowOnInProgress || !hasWriteAccess
              }
              size="medium"
          >
            <ArgosFolderDeleteIcon/>
          </IconButton>
          </Tooltip>}
        </ArgosFlexbox>
      ) : (
        <ArgosFlexbox>
          <IconButton
            disabled={shouldDisableRowOnInProgress || !hasWriteAccess}
            onClick={() => {
              const currentData: any = {
                "datasetId": props.selectedDatasetId,
                "category": row.original.category,
                "option": row.original.name,
                "rowData": row.original,
                "index": row.index
              }
              if (row.original.type == "Object") {
                currentData["id"] = row.original.objectId
              } else {
                currentData["id"] = row.original.name
              }
              setCurrentDataToEdit(currentData)
              setIsAddProperty(false)
              setPropertyType(row.original.type)
              setDialogTitle(`Edit ${row.original.type} Property`)
              if (row.original.type == "Capability" || row.original.type == "Use Case") {
                setProgramName(programName)
                setAdasProgramId(adasProgramId)
                setIsAddTreeNodeDialogOpen(true)
              }
                //setCurrentDataToEdit(currentData)
                //setIsAddProperty(false)
              //setPropertyType(row.original.type)
              else {

                setIsOpenDialog(true)
                setSelectedDialog(REDLINE_DIALOG_OPTIONS.EDIT_PROPERTY)
              }
            }}>
            <ArgosEditIcon/>
          </IconButton>
          <IconButton
            disabled={shouldDisableRow || !hasWriteAccess}
            onClick={() => handleButtonClicked("deleteProperty", row)}>
            <ArgosDeleteIcon/>
          </IconButton>
        </ArgosFlexbox>
      )
    )
  }

  /**
   * Modify table functionality
   */
    // We have the below two variables to avoid the case where delete is pressed twice on the same row, which causes other rows to be deleted. Instead, we exit out of the operation if the table data state has not yet been updated from a previous operation.
    // TODO: This might be needed in other places in argos as well. There might be a better solution if every item in table has a unique id.
  let isTableRowActionCurrentlyInProgress = false
  const refIsTableRowActionCurrentlyInProgress = React.useRef(false)

  const handleButtonClicked = (buttonType: string, _data: any) => {
    if (isTableRowActionCurrentlyInProgress || refIsTableRowActionCurrentlyInProgress.current) {
      return
    }
    isTableRowActionCurrentlyInProgress = true
    refIsTableRowActionCurrentlyInProgress.current = true
    switch (buttonType) {
      case REDLINE_DIALOG_OPTIONS.EDIT_PROPERTY:
        setIsOpenDialog(true)
        setSelectedDialog(buttonType)
        setPropertyType(_data.original.type)
        const currentData = {
          "datasetId": props.selectedDatasetId,
          "rowData": _data["original"],
          "index": _data["index"]
        }
        setCurrentDataToEdit(currentData)
        setDialogTitle("Edit Property")
        setIsAddProperty(false);
        break;
      case "deleteProperty":
        // using fancy operations did not work, needed to do it with good ol' for loop
        const propertiesFlatList = cloneDeep(props.propertiesFlatList)
        // If the row has subrows, delete all rows of the same type or category
        if ("subRows" in _data.original) {
          const indexSet = new Set();
          for (let i = 0; i < propertiesFlatList.length; i++) {
            const object = propertiesFlatList[i]
            // If the object is of the same type or category, add it to the index set
            if (("type" in _data.original && object.type == _data.original.type) || ("category" in _data.original && object.category == _data.original.category)) {
              indexSet.add(i)
            }
          }
          if (indexSet.size) {
            const sortedIndices = Array.from(indexSet).sort((a:any, b:any) => b - a); // Sort indices in descending order
            for (const index of sortedIndices) {
              propertiesFlatList.splice(index, 1)
            }
          }
        }
        else{
          // Individual row deletion
          let indexToDelete = -1;
          for (let i = 0; i < propertiesFlatList.length; i++) {
            const object = propertiesFlatList[i]

            if (object.name == _data.original.name) {
              indexToDelete = i
            }
          }
          propertiesFlatList.splice(indexToDelete, 1)
        }
        handleSaveModifiedProperties(cloneDeep(propertiesFlatList), false, undefined)
        break;
    }
    isTableRowActionCurrentlyInProgress = false
  }
  /**
   * Callback to update dataset information
   */
  const handleSaveModifiedProperties = (newPropertiesData: any, isAdd: boolean, index: any) => {
    props?.saveNewInfo(newPropertiesData, isAdd, index)
  }

  /**
   * Render table with single or merged props based on type
   */
  if (datasetType === SINGLE_DATASET_TYPE_STR) {
    return (
      <>
        <ArgosDialog
          {...ADAS_DIALOG_STYLE}
          open={isAddTreeNodeDialogOpen}
          onClose={() => {
            setIsAddTreeNodeDialogOpen(false)
          }}
          headerTitle={dialogTitle}
        >
          <AddTreeNodeDialog
            adasProgramId={adasProgramId}
            data={currentDataToEdit}
            isAdd={isAddProperty}
            propertyType={propertyType}
            onFinish={() => {
              setIsAddTreeNodeDialogOpen(false)
            }}
            onClose={() => {
              setIsAddTreeNodeDialogOpen(false)
            }}
            onSave={handleSaveModifiedProperties}
            isSingleSelect={true}
            fromApp={ERequirementsToolTreeDialogAppNames.COVERAGE_REQUIREMENTS}
          />
        </ArgosDialog>
        <Loader isLoading={props?.isLoading ?? false} wrapperStyle={LOADER_WRAPPER_STYLE}>
          <ArgosFlexbox
            style={{marginBottom: "20px", width: "100%", overflow: "auto"}}
            vertical
          >
            <div style={{width: "100%"}}>
              <ArgosTableSimple
                columns={singleDatasetColumns}
                data={data}

                enableExpanding
                muiExpandButtonProps={({row}) => ({
                  // Show expand button only when row has sub-elements
                  sx: {
                    display: row.subRows?.length ? 'flex' : 'none'
                  }
                })}
                enableRowActions={!shouldDisableRowOnInProgress}
                positionActionsColumn="last"
                getSubRows={(originalRow) => originalRow?.subRows}
                enablePagination={false}
                enableBottomToolbar={false}
                enableTableFooter={false}
                renderRowActions={({row}: any) => !isReadOnlyStudy && tableRowActions(row)}
                state={{isLoading: props?.isLoading}}
                muiTableHeadRowProps={{
                  // Header row style
                  sx: {backgroundColor: tableHeaderGray},
                }}
                displayColumnDefOptions={{
                  'mrt-row-actions': {
                    size: 200,
                  },
                }}
              />
              {
                isOpenDialog &&
                <RedlineAppDialog
                  open={isOpenDialog}
                  dialogtype={selectedDialog}
                  dialogtitle={dialogTitle}
                  data={currentDataToEdit}
                  multipleSelect={PROPERTY_TYPE_IS_MULTIPLE_SELECT[propertyType as keyof object]}
                  isAdd={isAddProperty}
                  propertyType={propertyType}
                  onSave={selectedDialog == "delCategoryGroup" ? handleButtonClicked : handleSaveModifiedProperties}
                  onClose={() => setIsOpenDialog(false)}
                />
              }
            </div>
          </ArgosFlexbox>
        </Loader>
      </>
    )
  } else {
    return (
      <>
        <Loader isLoading={props?.isLoading ?? false} wrapperStyle={LOADER_WRAPPER_STYLE}>
          <ArgosFlexbox style={{marginBottom: "20px", width: "100%", overflow: "auto"}} vertical>
            <ArgosTableSimple
              columns={mergedDatasetColumns}
              data={data}

              enableExpanding
              muiExpandButtonProps={({row}) => ({
                // Show expand button only when row has sub-elements
                sx: {
                  display: row.subRows?.length ? 'flex' : 'none'
                }
              })}
              getSubRows={(originalRow) => originalRow?.subRows}
              enablePagination={false}
              enableBottomToolbar={false}
              enableTableFooter={false}
              state={{isLoading: props?.isLoading}}
              muiTableHeadRowProps={{
                // Header row style
                sx: {backgroundColor: tableHeaderGray},
              }}
            />
          </ArgosFlexbox>
        </Loader>
      </>
    )
  }
}

export {DrivingScenarioProperties};

/**
 * Get the display name for an object
 * @param {string | null} objCategory - The category of the object.
 * @param {string | null} objName - The name of the object.
 * @param {string | null} objVariant - The variant of the object.
 * @returns {string} - The display name for the object.
 */
export const getObjectDisplayName = (objCategory: string | null, objName: string | null, objVariant: string | null, version: string | null = ""): string => {
  if (!objName || !objVariant) {
    return ""
  } else if (!objCategory) {
    return `${objName} ${objVariant ? `${version} (${getProfileDisplayName(objVariant as TPmoAndMiscProfileNames)})` : ""}`
  } else {
    return `${objCategory}: ${objName} ${objVariant ? `${version} (${getProfileDisplayName(objVariant as TPmoAndMiscProfileNames)})` : ""}`
  }
}
